import React, { useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Box, Button, FormControl, FormLabel, Heading, HStack, Input, Link, SimpleGrid, Text, VStack,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';

const errorText = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(' ; ');
  return err?.message || 'Erreur inconnue';
};

const STATUS_LABELS: Record<string, string> = {
  running: 'En cours',
  finished: 'Terminée',
  stopped: 'Arrêtée',
  failed: 'Échec',
};

const CheckinDemoPage: React.FC = () => {
  const qc = useQueryClient();
  const [count, setCount] = useState('80');
  const [paceMin, setPaceMin] = useState('2');
  const [paceMax, setPaceMax] = useState('10');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Polled every 2 s; the same endpoint decides whether the menu entry exists.
  const statusQ = useQuery({
    queryKey: ['checkin-simulation'],
    queryFn: () => apiService.getCheckinSimulation(),
    refetchInterval: 2000,
    staleTime: 0,
  });
  const status = statusQ.data;
  const exists = !!status && status.status !== 'none';

  const start = useMutation({
    mutationFn: () => apiService.startCheckinSimulation({
      count: Number(count),
      pace_min: Number(paceMin),
      pace_max: Number(paceMax),
    }),
    onSuccess: () => {
      setError(null);
      setMessage(null);
      qc.invalidateQueries({ queryKey: ['checkin-simulation'] });
    },
    onError: (err: any) => {
      if (err?.response?.status === 409) {
        setError(null);
        setMessage('Une simulation est déjà en cours');
        qc.invalidateQueries({ queryKey: ['checkin-simulation'] });
      } else {
        setMessage(null);
        setError(errorText(err));
      }
    },
  });

  const stop = useMutation({
    mutationFn: () => apiService.stopCheckinSimulation(),
    onSuccess: () => {
      setError(null);
      setMessage(null);
      qc.invalidateQueries({ queryKey: ['checkin-simulation'] });
    },
    onError: (err: any) => setError(errorText(err)),
  });

  const onStop = () => {
    if (window.confirm('Arrêter la simulation et supprimer l\'événement de démonstration et ses invités ?')) {
      stop.mutate();
    }
  };

  const running = status?.status === 'running';

  return (
    <Box maxW="3xl">
      <Heading size="lg" mb={2}>Démo check-in</Heading>
      <Text color="gray.600" mb={4} data-testid="demo-note">
        Staging uniquement. Utilise de vrais contacts, n'envoie aucun e-mail.
      </Text>

      {message && (
        <Alert status="warning" borderRadius="md" mb={4} data-testid="demo-message">
          <AlertIcon />
          <AlertDescription fontSize="sm">{message}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert status="error" borderRadius="md" mb={4} data-testid="demo-error">
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      <HStack spacing={4} align="flex-end" mb={6} flexWrap="wrap">
        <FormControl w="150px">
          <FormLabel fontSize="sm">Nombre d'invités</FormLabel>
          <Input type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} data-testid="demo-count" />
        </FormControl>
        <FormControl w="150px">
          <FormLabel fontSize="sm">Rythme min (s)</FormLabel>
          <Input type="number" min={0} value={paceMin} onChange={(e) => setPaceMin(e.target.value)} data-testid="demo-pace-min" />
        </FormControl>
        <FormControl w="150px">
          <FormLabel fontSize="sm">Rythme max (s)</FormLabel>
          <Input type="number" min={0} value={paceMax} onChange={(e) => setPaceMax(e.target.value)} data-testid="demo-pace-max" />
        </FormControl>
        <Button colorScheme="blue" onClick={() => start.mutate()} isDisabled={start.isPending || running}>
          Démarrer
        </Button>
      </HStack>

      {exists && status && (
        <VStack align="stretch" spacing={4}>
          <Text fontSize="sm" data-testid="demo-status">Statut : {STATUS_LABELS[status.status] || status.status}</Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontSize="xs" color="gray.500">Check-ins</Text>
              <Text fontSize="2xl" fontWeight="bold" data-testid="demo-checkins">{status.checkins ?? 0}</Text>
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontSize="xs" color="gray.500">Annulations</Text>
              <Text fontSize="2xl" fontWeight="bold" data-testid="demo-cancellations">{status.cancellations ?? 0}</Text>
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontSize="xs" color="gray.500">Erreurs</Text>
              <Text fontSize="2xl" fontWeight="bold" data-testid="demo-errors">{status.errors ?? 0}</Text>
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Text fontSize="xs" color="gray.500">Prévus</Text>
              <Text fontSize="2xl" fontWeight="bold" data-testid="demo-planned">{status.planned ?? 0}</Text>
            </Box>
          </SimpleGrid>
          <HStack spacing={4}>
            {status.vip_board_url && (
              <Link href={status.vip_board_url} isExternal target="_blank" rel="noopener noreferrer" data-testid="demo-vip-link" color="blue.600">
                Ouvrir le VIP board
              </Link>
            )}
            <Button colorScheme="red" variant="outline" onClick={onStop} isDisabled={stop.isPending}>
              Arrêter et nettoyer
            </Button>
          </HStack>
        </VStack>
      )}
    </Box>
  );
};

export default CheckinDemoPage;
